import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { BorderedGrid } from '@shared/components/borderedGrid';
import {
  ConversationSettingsDraft,
  ConversationSettingsValidation,
  DelayDraft,
  PreemptiveGenerationDraft,
  SpeechPacingDraft,
  TurnHandlingDraft,
} from '../utils/conversationSettings.utils';

type Props = {
  value: ConversationSettingsDraft | null;
  onChange: (value: ConversationSettingsDraft) => void;
  errors?: ConversationSettingsValidation;
  disabled?: boolean;
};

const emptyTurnHandling = (): TurnHandlingDraft => ({
  minEndpointingDelay: '',
  maxEndpointingDelay: '',
  turnDetection: '',
  minInterruptionWords: '',
  minInterruptionDuration: '',
  falseInterruptionTimeout: '',
});

const emptySpeechPacing = (): SpeechPacingDraft => ({
  enabled: false,
  phraseBoundaryDelayMs: { mode: 'range', scalar: '', min: '', max: '' },
  sentenceBoundaryDelayMs: { mode: 'range', scalar: '', min: '', max: '' },
  seed: '',
});

const emptyPreemptive = (): PreemptiveGenerationDraft => ({
  enabled: false,
  preemptiveTts: false,
  maxSpeechDuration: '',
  maxRetries: '',
});

export const ConversationSettingsForm: React.FC<Props> = ({
  value,
  onChange,
  errors = { turnHandling: {}, speechPacing: {} },
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [expandedTurn, setExpandedTurn] = useState(false);
  const [expandedPacing, setExpandedPacing] = useState(false);
  const [expandedPreemptive, setExpandedPreemptive] = useState(false);

  useEffect(() => {
    setExpandedTurn(Boolean(value?.turnHandling));
    setExpandedPacing(Boolean(value?.speechPacing?.enabled));
    setExpandedPreemptive(Boolean(value?.turnHandling?.preemptiveGeneration?.enabled));
  }, [
    value?.speechPacing?.enabled,
    value?.turnHandling,
    value?.turnHandling?.preemptiveGeneration?.enabled,
  ]);

  if (!value) return null;

  const turn = value.turnHandling ?? emptyTurnHandling();
  const pacing = value.speechPacing ?? emptySpeechPacing();
  const preemptive = turn.preemptiveGeneration ?? emptyPreemptive();
  const updateTurn = (next: TurnHandlingDraft) => onChange({ ...value, turnHandling: next });
  const updatePacing = (next: SpeechPacingDraft) => onChange({ ...value, speechPacing: next });
  const updatePreemptive = (next: PreemptiveGenerationDraft) =>
    updateTurn({ ...turn, preemptiveGeneration: next });
  const updateDelay = (
    key: 'phraseBoundaryDelayMs' | 'sentenceBoundaryDelayMs',
    next: DelayDraft,
  ) => updatePacing({ ...pacing, [key]: next });

  const textField = (
    label: string,
    currentValue: string,
    onValueChange: (value: string) => void,
    options?: { step?: number; disabled?: boolean; error?: string; helper?: string },
  ) => (
    <TextField
      fullWidth
      size="small"
      type={options?.step !== undefined ? 'number' : 'text'}
      label={label}
      value={currentValue}
      disabled={disabled || options?.disabled}
      error={Boolean(options?.error)}
      helperText={options?.error ? t(options.error) : options?.helper}
      inputProps={
        options?.step !== undefined
          ? {
              min: 0,
              step: options.step,
              onWheel: (event: React.WheelEvent<HTMLInputElement>) => {
                event.currentTarget.blur();
              },
            }
          : undefined
      }
      onChange={(event) => onValueChange(event.target.value)}
    />
  );

  const switchField = (
    label: string,
    checked: boolean,
    onCheckedChange: (checked: boolean) => void,
    helper?: string,
    fieldDisabled = false,
  ) => (
    <Box>
      <FormControlLabel
        control={
          <CustomSwitch
            checked={checked}
            disabled={disabled || fieldDisabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onCheckedChange(event.target.checked)
            }
          />
        }
        label={label}
        sx={{ ml: 0, gap: 1 }}
      />
      {helper ? <FormHelperText sx={{ ml: 0 }}>{helper}</FormHelperText> : null}
    </Box>
  );

  const rangeField = (
    label: string,
    delay: DelayDraft,
    onDelayChange: (next: DelayDraft) => void,
    errorPrefix: string,
    step: number,
    fieldDisabled: boolean,
    helper?: string,
  ) => (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>{t('conversationSettings.rangeMode')}</InputLabel>
          <Select
            label={t('conversationSettings.rangeMode')}
            value={delay.mode}
            disabled={disabled || fieldDisabled}
            onChange={(event) =>
              onDelayChange({ ...delay, mode: event.target.value as DelayDraft['mode'] })
            }>
            <MenuItem value="scalar">{t('conversationSettings.scalar')}</MenuItem>
            <MenuItem value="range">{t('conversationSettings.range')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      {delay.mode === 'range' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
            {textField(
              t('conversationSettings.minMs'),
              delay.min,
              (next) => onDelayChange({ ...delay, min: next }),
              {
                step,
                disabled: fieldDisabled,
                error: errors.speechPacing[`${errorPrefix}.min`],
              },
            )}
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
            {textField(
              t('conversationSettings.maxMs'),
              delay.max,
              (next) => onDelayChange({ ...delay, max: next }),
              {
                step,
                disabled: fieldDisabled,
                error: errors.speechPacing[`${errorPrefix}.max`],
              },
            )}
          </Box>
        </Stack>
      ) : (
        textField(label, delay.scalar, (next) => onDelayChange({ ...delay, scalar: next }), {
          step,
          disabled: fieldDisabled,
          error: errors.speechPacing[errorPrefix],
        })
      )}
      {helper ? <FormHelperText sx={{ ml: 0 }}>{helper}</FormHelperText> : null}
    </Stack>
  );

  return (
    <Box
      sx={{
        width: '760px',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        pt: 3,
      }}>
      <BorderedGrid sx={{ p: 0, overflow: 'hidden' }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expandedTurn}
          onChange={(_, expanded) => {
            setExpandedTurn(expanded);
          }}
          sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="conversation-turn-handling-content"
            id="conversation-turn-handling-header"
            sx={{ minHeight: 72, px: 2.5, '& .MuiAccordionSummary-content': { mr: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Box>
                <Typography fontSize="16px" fontWeight={600}>
                  {t('conversationSettings.turnHandling.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('conversationSettings.turnHandling.description')}
                </Typography>
              </Box>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            id="conversation-turn-handling-content"
            sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    {textField(
                      t('conversationSettings.turnHandling.minEndpointingDelay'),
                      turn.minEndpointingDelay,
                      (next) => updateTurn({ ...turn, minEndpointingDelay: next }),
                      {
                        step: 0.1,
                        disabled,
                        error: errors.turnHandling.minEndpointingDelay,
                      },
                    )}
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    {textField(
                      t('conversationSettings.turnHandling.maxEndpointingDelay'),
                      turn.maxEndpointingDelay,
                      (next) => updateTurn({ ...turn, maxEndpointingDelay: next }),
                      {
                        step: 0.1,
                        disabled,
                        error: errors.turnHandling.maxEndpointingDelay,
                      },
                    )}
                  </Box>
                </Stack>
                <FormHelperText sx={{ ml: 0 }}>
                  {t('conversationSettings.turnHandling.endpointingDelayHelp')}
                </FormHelperText>
              </Stack>
              <FormControl
                sx={{ width: { xs: '100%', sm: '50%' } }}
                size="small"
                disabled={disabled}>
                <InputLabel id="conversation-turn-detection-label">
                  {t('conversationSettings.turnHandling.turnDetection')}
                </InputLabel>
                <Select
                  labelId="conversation-turn-detection-label"
                  label={t('conversationSettings.turnHandling.turnDetection')}
                  value={turn.turnDetection ?? ''}
                  inputProps={{ 'aria-describedby': 'conversation-turn-detection-help' }}
                  onChange={(event) => updateTurn({ ...turn, turnDetection: event.target.value })}>
                  <MenuItem value="">
                    <em>{t('conversationSettings.notSet')}</em>
                  </MenuItem>
                  <MenuItem
                    value="vad"
                    title={t('conversationSettingsOptions.turnDetectionDescriptions.vad')}>
                    {t('conversationSettingsOptions.turnDetection.vad')}
                  </MenuItem>
                  <MenuItem
                    value="stt"
                    title={t('conversationSettingsOptions.turnDetectionDescriptions.stt')}>
                    {t('conversationSettingsOptions.turnDetection.stt')}
                  </MenuItem>
                  <MenuItem
                    value="manual"
                    title={t('conversationSettingsOptions.turnDetectionDescriptions.manual')}>
                    {t('conversationSettingsOptions.turnDetection.manual')}
                  </MenuItem>
                  <MenuItem
                    value="realtime_llm"
                    disabled
                    title={t('conversationSettingsOptions.turnDetectionDescriptions.realtimeLlm')}>
                    {t('conversationSettingsOptions.turnDetection.realtimeLlm')}
                  </MenuItem>
                </Select>
                <FormHelperText id="conversation-turn-detection-help">
                  {t('conversationSettings.turnHandling.turnDetectionHelp')}
                </FormHelperText>
              </FormControl>
              <FormControl
                sx={{ width: { xs: '100%', sm: '50%' } }}
                size="small"
                disabled={disabled}>
                <InputLabel>{t('conversationSettings.turnHandling.endpointingMode')}</InputLabel>
                <Select
                  label={t('conversationSettings.turnHandling.endpointingMode')}
                  value={turn.endpointingMode ?? ''}
                  onChange={(event) =>
                    updateTurn({
                      ...turn,
                      endpointingMode: (event.target.value ||
                        undefined) as TurnHandlingDraft['endpointingMode'],
                    })
                  }>
                  <MenuItem value="">
                    <em>{t('conversationSettings.notSet')}</em>
                  </MenuItem>
                  <MenuItem value="fixed">
                    {t('conversationSettingsOptions.endpointingMode.fixed')}
                  </MenuItem>
                  <MenuItem value="dynamic">
                    {t('conversationSettingsOptions.endpointingMode.dynamic')}
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {t('conversationSettings.turnHandling.endpointingModeHelp')}
                </FormHelperText>
              </FormControl>
              {switchField(
                t('conversationSettings.turnHandling.interruptionEnabled'),
                Boolean(turn.interruptionEnabled),
                (checked) => updateTurn({ ...turn, interruptionEnabled: checked }),
                t('conversationSettings.turnHandling.interruptionEnabledHelp'),
                disabled,
              )}
              <FormControl
                sx={{ width: { xs: '100%', sm: '50%' } }}
                size="small"
                disabled={disabled}>
                <InputLabel>{t('conversationSettings.turnHandling.interruptionMode')}</InputLabel>
                <Select
                  label={t('conversationSettings.turnHandling.interruptionMode')}
                  value={turn.interruptionMode ?? ''}
                  onChange={(event) =>
                    updateTurn({
                      ...turn,
                      interruptionMode: (event.target.value ||
                        undefined) as TurnHandlingDraft['interruptionMode'],
                    })
                  }>
                  <MenuItem value="">
                    <em>{t('conversationSettings.notSet')}</em>
                  </MenuItem>
                  <MenuItem value="vad">
                    {t('conversationSettingsOptions.interruptionMode.vad')}
                  </MenuItem>
                  <MenuItem value="adaptive">
                    {t('conversationSettingsOptions.interruptionMode.adaptive')}
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {t('conversationSettings.turnHandling.interruptionModeHelp')}
                </FormHelperText>
              </FormControl>
              <Stack spacing={0.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    {textField(
                      t('conversationSettings.turnHandling.minInterruptionWords'),
                      turn.minInterruptionWords,
                      (next) => updateTurn({ ...turn, minInterruptionWords: next }),
                      {
                        step: 1,
                        disabled,
                        error: errors.turnHandling.minInterruptionWords,
                      },
                    )}
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    {textField(
                      t('conversationSettings.turnHandling.minInterruptionDuration'),
                      turn.minInterruptionDuration,
                      (next) => updateTurn({ ...turn, minInterruptionDuration: next }),
                      {
                        step: 0.1,
                        disabled,
                        error: errors.turnHandling.minInterruptionDuration,
                      },
                    )}
                  </Box>
                </Stack>
                <FormHelperText sx={{ ml: 0 }}>
                  {t('conversationSettings.turnHandling.interruptionThresholdHelp')}
                </FormHelperText>
              </Stack>
              {textField(
                t('conversationSettings.turnHandling.falseInterruptionTimeout'),
                turn.falseInterruptionTimeout,
                (next) => updateTurn({ ...turn, falseInterruptionTimeout: next }),
                {
                  step: 0.1,
                  disabled,
                  error: errors.turnHandling.falseInterruptionTimeout,
                  helper: t('conversationSettings.turnHandling.falseInterruptionTimeoutHelp'),
                },
              )}
              {switchField(
                t('conversationSettings.turnHandling.resumeFalseInterruption'),
                Boolean(turn.resumeFalseInterruption),
                (checked) => updateTurn({ ...turn, resumeFalseInterruption: checked }),
                t('conversationSettings.turnHandling.resumeFalseInterruptionHelp'),
                disabled,
              )}
              <Accordion
                disableGutters
                elevation={0}
                expanded={expandedPreemptive}
                onChange={(_, expanded) => setExpandedPreemptive(expanded)}
                sx={{ '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="conversation-preemptive-content"
                  id="conversation-preemptive-header">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%">
                    <Typography fontWeight={600}>
                      {t('conversationSettings.turnHandling.preemptiveGeneration.title')}
                    </Typography>
                    <CustomSwitch
                      checked={preemptive.enabled}
                      disabled={disabled}
                      inputProps={{
                        'aria-label': t(
                          'conversationSettings.turnHandling.preemptiveGeneration.enabled',
                        ),
                      }}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        updatePreemptive({ ...preemptive, enabled: event.target.checked });
                        setExpandedPreemptive(event.target.checked);
                      }}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails id="conversation-preemptive-content">
                  <Stack spacing={2}>
                    <FormHelperText sx={{ ml: 0 }}>
                      {t('conversationSettings.turnHandling.preemptiveGeneration.enabledHelp')}
                    </FormHelperText>
                    {switchField(
                      t('conversationSettings.turnHandling.preemptiveGeneration.preemptiveTts'),
                      preemptive.preemptiveTts,
                      (checked) => updatePreemptive({ ...preemptive, preemptiveTts: checked }),
                      t('conversationSettings.turnHandling.preemptiveGeneration.preemptiveTtsHelp'),
                      disabled || !preemptive.enabled,
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                        {textField(
                          t(
                            'conversationSettings.turnHandling.preemptiveGeneration.maxSpeechDuration',
                          ),
                          preemptive.maxSpeechDuration,
                          (next) => updatePreemptive({ ...preemptive, maxSpeechDuration: next }),
                          {
                            step: 0.1,
                            disabled: disabled || !preemptive.enabled,
                            error: errors.turnHandling.maxSpeechDuration,
                          },
                        )}
                      </Box>
                      <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                        {textField(
                          t('conversationSettings.turnHandling.preemptiveGeneration.maxRetries'),
                          preemptive.maxRetries,
                          (next) => updatePreemptive({ ...preemptive, maxRetries: next }),
                          {
                            step: 1,
                            disabled: disabled || !preemptive.enabled,
                            error: errors.turnHandling.maxRetries,
                          },
                        )}
                      </Box>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </BorderedGrid>

      <BorderedGrid sx={{ p: 0, overflow: 'hidden' }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expandedPacing}
          onChange={(_, expanded) => setExpandedPacing(expanded)}
          sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="conversation-speech-pacing-content"
            id="conversation-speech-pacing-header"
            sx={{ minHeight: 72, px: 2.5, '& .MuiAccordionSummary-content': { mr: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Box>
                <Typography fontSize="16px" fontWeight={600}>
                  {t('conversationSettings.speechPacing.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('conversationSettings.speechPacing.description')}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CustomSwitch
                  checked={Boolean(pacing.enabled)}
                  disabled={disabled}
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    updatePacing({ ...pacing, enabled: event.target.checked });
                    setExpandedPacing(event.target.checked);
                  }}
                />
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            id="conversation-speech-pacing-content"
            sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <Stack spacing={2}>
              <FormHelperText>{t('conversationSettings.speechPacing.enabledHelp')}</FormHelperText>
              {textField(
                t('conversationSettings.speechPacing.seed'),
                pacing.seed,
                (next) => updatePacing({ ...pacing, seed: next }),
                {
                  disabled: disabled || !pacing.enabled,
                  error: errors.speechPacing.seed,
                  helper: t('conversationSettings.speechPacing.seedHelp'),
                },
              )}
              {rangeField(
                t('conversationSettings.speechPacing.phraseBoundaryDelayMs.title'),
                pacing.phraseBoundaryDelayMs,
                (next) => updateDelay('phraseBoundaryDelayMs', next),
                'phraseBoundaryDelayMs',
                10,
                !pacing.enabled,
                t('conversationSettings.speechPacing.phraseBoundaryDelayMs.help'),
              )}
              {rangeField(
                t('conversationSettings.speechPacing.sentenceBoundaryDelayMs.title'),
                pacing.sentenceBoundaryDelayMs,
                (next) => updateDelay('sentenceBoundaryDelayMs', next),
                'sentenceBoundaryDelayMs',
                10,
                !pacing.enabled,
                t('conversationSettings.speechPacing.sentenceBoundaryDelayMs.help'),
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </BorderedGrid>
    </Box>
  );
};
